import React, { ChangeEventHandler, useCallback, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  ExtendedMappedInternalRoom,
  InternalRoom,
  MappedInternalRoom,
  Room,
  RoomMappingPayload,
} from '~/services/AccommodationService';

interface RoomWithPackage extends Room {
  hasActiveLpcPackage?: boolean;
}

import ControlRoomElement from './ControlRoomElement';

interface Props {
  propertyRooms: Array<RoomWithPackage>;
  selectedControlRoom: RoomWithPackage | null;
  selectedInternalRooms: Set<string>;
  appliedButNotSavedRooms: Array<InternalRoom>;
  handleCreateAndMapRoom: () => void;
  handleCreateAndMapMultipleRooms: () => void;
  handleRoomSelection: (room: RoomWithPackage) => void;
  handleUpdateMapping: () => void;
  handleVerifyMappedRoom: (mappedRoom: MappedInternalRoom) => void;
  clearNewMappings: (room: RoomWithPackage) => void;
  handleRemove: (mappedRoom: ExtendedMappedInternalRoom) => void;
  updatePayload: RoomMappingPayload;
  removePayload: RoomMappingPayload;
}

export default function MappedRooms({
  propertyRooms,
  selectedControlRoom,
  selectedInternalRooms,
  appliedButNotSavedRooms,
  handleCreateAndMapRoom,
  handleCreateAndMapMultipleRooms,
  handleRoomSelection,
  handleUpdateMapping,
  handleVerifyMappedRoom,
  clearNewMappings,
  handleRemove,
  updatePayload,
  removePayload,
}: Props) {
  const [filteringText, setFilteringText] = useState('');
  const [showRoomsMissingBedbank, setShowRoomsMissingBedbank] = useState(false);

  const resetFilteringText = useCallback(() => {
    setFilteringText('');
  }, []);

  const handleFilteringTextInput = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setFilteringText(event.target.value);
  }, []);

  const handleCheckboxChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setShowRoomsMissingBedbank(event.target.checked);
  }, []);

  const filteredPropertyRooms = useMemo(() => {
    return propertyRooms.filter((room) => {
      const matchesText = room.name.toLowerCase().includes(filteringText.toLowerCase());
      const matchesBedbank = showRoomsMissingBedbank
        ? !room.mappedRooms.some((mr) => mr.internalService.toLowerCase() === 'bedbank')
        : true;
      return matchesText && matchesBedbank;
    });
  }, [propertyRooms, filteringText, showRoomsMissingBedbank]);

  const selectedFromPropertyRooms = useMemo(() => {
    const totalCount = propertyRooms.length;

    const totalWithMappedRooms = propertyRooms.filter((room) => room.mappedRooms.length > 0).length;

    const totalWithoutMappedRooms = totalCount - totalWithMappedRooms;

    return selectedControlRoom
      ? `${selectedControlRoom.name} selected`
      : `${totalWithMappedRooms} ${pluralize(
          'rooms',
          totalWithMappedRooms,
        )} with mapping, ${totalWithoutMappedRooms} ${pluralize('rooms', totalWithoutMappedRooms)} without mapping`;
  }, [propertyRooms, selectedControlRoom]);

  return (
    <Stack direction="column" gap={2}>
      <Paper square sx={{ position: 'sticky', top: 0, zIndex: 1, pt: 1 }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h6">Property Rooms: {selectedFromPropertyRooms}</Typography>
          <TextField
            label="Filter by room name, room ID, or mapped room ID:"
            value={filteringText}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={resetFilteringText} disabled={filteringText.length < 1} title="clear" edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onInput={handleFilteringTextInput}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showRoomsMissingBedbank}
                onChange={handleCheckboxChange}
                name="showRoomsMissingBedbank"
              />
            }
            label="Show rooms missing bedbank"
          />
          <Button
            variant="contained"
            startIcon={<AccountTreeIcon />}
            title="Map selected internal rooms to the selected Room"
            onClick={handleUpdateMapping}
            disabled={Array.from(selectedInternalRooms.entries()).length < 1 || !selectedControlRoom}
          >
            Apply selected Mapping
          </Button>
          <Button
            variant="contained"
            title="Create new property room"
            onClick={handleCreateAndMapRoom}
            disabled={Array.from(selectedInternalRooms.entries()).length < 1 || !!selectedControlRoom}
          >
            Map to new Property Room
          </Button>
          <Button
            variant="contained"
            title="Map to multiple new Property Rooms"
            onClick={handleCreateAndMapMultipleRooms}
            disabled={Array.from(selectedInternalRooms.entries()).length < 1 || !!selectedControlRoom}
          >
            Map to multiple new Property Rooms
          </Button>
          <Divider />
        </Stack>
      </Paper>
      <Stack direction="column" gap={2}>
        {filteredPropertyRooms?.map((room) => (
          <ControlRoomElement
            key={room.id}
            room={room}
            isSelected={selectedControlRoom?.id === room?.id}
            onSelect={handleRoomSelection}
            onRemoveMappedRoom={handleRemove}
            onVerifyMappedRoom={handleVerifyMappedRoom}
            clearNewMappings={() => clearNewMappings(room)}
            newMappings={updatePayload.find((r) => r.roomId === room.id)}
            unsavedInternalRooms={appliedButNotSavedRooms}
            removePayload={removePayload}
          />
        ))}
        {filteringText && filteredPropertyRooms.length === 0 && <Typography>No rooms found based on filter</Typography>}
      </Stack>
    </Stack>
  );
}
